import type { ReactNode } from 'react';
import { Card } from '@/components/ui/Card';

type Props = {
  sessionLoading: boolean;
  currentSiteId: number;
  apiLoading: boolean;
  /** 本页请求可能较慢时的补充说明（如微信公众号页） */
  slowRequestHint?: string;
};

function StepRow({
  done,
  active,
  children,
}: {
  done: boolean;
  active: boolean;
  children: ReactNode;
}) {
  return (
    <li
      className={
        done
          ? 'flex gap-2 text-emerald-800'
          : active
            ? 'flex gap-2 font-medium text-teal-800'
            : 'flex gap-2 text-slate-500'
      }
    >
      <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px] leading-none">
        {done ? '✓' : active ? <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-teal-500" aria-hidden /> : '…'}
      </span>
      <span>{children}</span>
    </li>
  );
}

/**
 * 设置类页面：在会话或接口未就绪时展示分步进度，避免整页空白转圈被误认为卡死。
 */
export function PageSetupProgressCard({ sessionLoading, currentSiteId, apiLoading, slowRequestHint }: Props) {
  const siteReady = currentSiteId >= 1;
  const allDone = !sessionLoading && siteReady && !apiLoading;
  if (allDone) return null;

  const siteMissing = !sessionLoading && !siteReady;

  return (
    <Card title="加载进度">
      <ol className="list-none space-y-3 p-0">
        <StepRow done={!sessionLoading} active={sessionLoading}>
          {sessionLoading ? '正在确认登录状态与当前站点…' : '已确认登录与当前站点'}
        </StepRow>

        {siteMissing ? (
          <p className="rounded-lg border border-amber-200/80 bg-amber-50/90 px-3 py-2 text-sm text-amber-950">
            当前没有可用站点。请先在侧栏选择站点，或按指引创建站点后再打开本页。
          </p>
        ) : (
          <StepRow done={!sessionLoading && siteReady && !apiLoading} active={!sessionLoading && siteReady && apiLoading}>
            {!sessionLoading && siteReady && apiLoading
              ? '正在从服务器读取本页配置…'
              : !sessionLoading && siteReady && !apiLoading
                ? '本页配置已就绪'
                : '等待站点就绪后将加载本页配置'}
          </StepRow>
        )}
      </ol>

      {slowRequestHint && !sessionLoading && siteReady && apiLoading ? (
        <p className="mt-4 border-t border-slate-100 pt-3 text-xs leading-relaxed text-slate-600">{slowRequestHint}</p>
      ) : null}
    </Card>
  );
}
